import React, { useState, useEffect, useRef } from "react";
import "./SubtitleNotFoundModal.css";
import { useVideo } from "../../context/VideoContext";
import { useSelectedSubtitles } from "../../context/SelectedSubtitlesContext";
import { findCollectionSubtitlesByInfo } from "../../services/subtitleApi";
import { safeSetItem } from "../../utils/safeStorage";
import useVideoManager from "../../hooks/useVideoManager";
import { safeGetItem } from "../../utils/safeStorage";
import { getLanguageName } from "../../utils/languages";
import useVideoMetadata from "../../hooks/useVideoMetadata";
import CollectionConfirmModal from "../CollectionSubtitles/CollectionConfirmModal";

const SubtitleNotFoundModal = ({
  onClose,
  onSearch,
  movieName,
  videoMetadata,
  suggestedSubtitles,
}) => {
  const { setShortcutsEnabled, setShowTutorialGuide } = useVideo();
  const { subtitleHistory } = useSelectedSubtitles();
  const { loadSubtitlesFromIds } = useVideoManager();
  const videoMetadataManager = useVideoMetadata();

  // بررسی "دیگر نشان نده"
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // آکوردین پیشنهاد دیگر کاربران
  const [showSuggestions, setShowSuggestions] = useState(true);

  // فرم جستجو
  const [searchMovieName, setSearchMovieName] = useState("");
  const [searchSeason, setSearchSeason] = useState("");
  const [searchEpisode, setSearchEpisode] = useState("");

  // نتایج جستجو
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // مودال تأیید همخوانی
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [currentCollectionIds, setCurrentCollectionIds] = useState({
    subOneID: null,
    subTwoID: null,
  });
  const [isLoadingSubs, setIsLoadingSubs] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const resultsRef = useRef(null);

  const suggestions = Array.isArray(suggestedSubtitles) ? suggestedSubtitles : [];

  // غیرفعال کردن شورتکات‌ها هنگام باز بودن مودال
  useEffect(() => {
    setShortcutsEnabled(false);
    return () => {
      setShortcutsEnabled(true);
    };
  }, [setShortcutsEnabled]);

  // پر کردن فرم با اطلاعات استخراج‌شده از نام فایل
  useEffect(() => {
    const metadata =
      videoMetadata || videoMetadataManager.videoMetadata;
    const extractedInfo = videoMetadataManager.extractInfoFromFileName(
      metadata?.fileName || movieName || "",
    );

    setSearchMovieName(metadata?.filmName || extractedInfo?.filmName || "");
    setSearchSeason(metadata?.season || extractedInfo?.season || "");
    setSearchEpisode(metadata?.episode || extractedInfo?.episode || "");
  }, [movieName, videoMetadata]);

  // جستجوی خودکار هنگام باز شدن مودال
  useEffect(() => {
    const metadata = videoMetadata || videoMetadataManager.videoMetadata;
    const extractedInfo = videoMetadataManager.extractInfoFromFileName(
      metadata?.fileName || movieName || "",
    );
    const autoName =
      metadata?.filmName || extractedInfo?.filmName || "";
    const autoSeason = metadata?.season || extractedInfo?.season || "";
    const autoEpisode = metadata?.episode || extractedInfo?.episode || "";

    if (autoName) {
      performSearch(autoName, autoSeason, autoEpisode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // تابع اجرای جستجو
  const performSearch = async (name, season, episode) => {
    if (!name || !name.trim()) {
      setSearchError("لطفاً نام فیلم یا سریال را وارد کنید");
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setSearchResults(null);

    try {
      const response = await findCollectionSubtitlesByInfo(
        name.trim(),
        season,
        episode,
      );

      if (response.success) {
        setSearchResults(response.data);
      } else {
        setSearchError(response.message || "خطا در جستجو");
      }
    } catch (err) {
      setSearchError("خطا در ارتباط با سرور. للطفاً دوباره تلاش کنید.");
      console.error("Search error:", err);
    } finally {
      setIsSearching(false);
    }
  };

  // هندلر جستجوی دستی کاربر
  const handleSearch = () => {
    performSearch(searchMovieName, searchSeason, searchEpisode);
  };

  // اسکرول به نتایج
  useEffect(() => {
    if (searchResults?.docs?.length && resultsRef.current) {
      const timer = setTimeout(() => {
        resultsRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [searchResults]);

  // لود و افزودن زیرنویس‌ها به پلیر
  const loadAndAddSubtitles = async (item) => {
    try {
      setIsLoadingSubs(true);
      setLoadError(null);

      await loadSubtitlesFromIds(
        item.subOneID,
        item.subTwoID,
        item.langOne,
        item.langTwo,
        item,
      );

      setCurrentCollectionIds({
        subOneID: item.subOneID,
        subTwoID: item.subTwoID,
      });

      if (item.movieName) {
        videoMetadataManager.updateMetadataFromBackend(
          { success: true, data: { docs: [item] } },
          videoMetadataManager.videoMetadata?.fileName || movieName || "",
        );
      }
    } catch (err) {
      console.error("🔴 Load subtitles error:", err);
      setLoadError("خطا در بارگذاری زیرنویس‌ها");
      throw err;
    } finally {
      setIsLoadingSubs(false);
    }
  };

  // کلیک روی افزودن - منتظر لود کامل
  const handleAddClick = async (item, e) => {
    e.stopPropagation();
    setSelectedCollection(item);
    setLoadError(null);
    await loadAndAddSubtitles(item);
    setShowConfirmModal(true);
  };

  // تأیید همخوانی - بستن کامل
  const handleConfirm = () => {
    setShowConfirmModal(false);
    setSelectedCollection(null);
    const tutorialDismissed = safeGetItem("tutorialGuideDismissedAt");
    if (!tutorialDismissed) {
      setShowTutorialGuide(true);
    }
    onClose();
  };

  // برگشت - موندن در مودال
  const handleBack = () => {
    setShowConfirmModal(false);
    setSelectedCollection(null);
  };

  // بستن با ذخیره تنظیم "دیگر نشان نده"
  const handleClose = () => {
    if (dontShowAgain) {
      safeSetItem("subtitleNotFoundSetting", "dont-show");
    }
    onClose();
  };

  // رندر کارت مجموعه (مشترک برای پیشنهادها و نتایج جستجو)
  const renderCollectionCard = (item, index) => {
    const isCurrentCollection =
      currentCollectionIds.subOneID === item.subOneID &&
      currentCollectionIds.subTwoID === item.subTwoID;
    const isInHistory = subtitleHistory.some(
      (h) => h.collectionId === item.collectionId,
    );

    let cardClass = "snfm-collection-card";
    if (isCurrentCollection) cardClass += " current-collection";
    if (isInHistory && !isCurrentCollection) cardClass += " in-history";

    return (
      <div key={item._id || index} className={cardClass}>
        <div className="snfm-card-header">
          {item.coverUrl && (
            <img
              src={item.coverUrl}
              alt={item.movieName}
              className="snfm-card-cover"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          )}
          <div className="snfm-card-info">
            <h4 className="snfm-card-title">
              {item.movieName}
              {isCurrentCollection && (
                <span className="snfm-current-badge">✓ فعال</span>
              )}
              {isInHistory && !isCurrentCollection && (
                <span className="snfm-history-badge">تاریخچه</span>
              )}
            </h4>

            <div className="snfm-card-metadata">
              <span className="snfm-video-type">
                {item.videoType === "serial" ? "📺 سریال" : "🎬 فیلم"}
              </span>
              {item.videoType === "serial" && item.season && (
                <span className="snfm-season-episode">
                  فصل {item.season}
                  {item.episode ? ` - قسمت ${item.episode}` : ""}
                </span>
              )}
            </div>

            <div className="snfm-card-languages">
              <span className="snfm-lang-one">
                {getLanguageName(item.langOne)}
              </span>
              <span className="snfm-lang-two">
                {getLanguageName(item.langTwo)}
              </span>
            </div>
          </div>
        </div>

        <button
          className="snfm-add-btn"
          onClick={(e) => handleAddClick(item, e)}
          disabled={isLoadingSubs}
        >
          {isLoadingSubs ? "⏳ در حال بارگذاری..." : "➕ افزودن به فیلم"}
        </button>
      </div>
    );
  };

  return (
    <>
      {/* 🎯 وقتی مودال تأیید بازه، اورلی اصلی رو پنهان می‌کنیم تا کاربر بتونه با ویدئو تعامل کنه */}
      <div
        className="subtitle-notfound-modal-overlay"
        style={showConfirmModal ? { display: "none" } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="subtitle-notfound-modal"
          onClick={(e) => e.stopPropagation()}
        >
          {/* هدر */}
          <div className="snfm-header">
            <div className="snfm-header-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="snfm-header-text">
              <h3 className="snfm-title">زیرنویس مناسب پیدا نشد</h3>
              <p className="snfm-subtitle-msg">
                {movieName ? (
                  <>
                    برای فیلم <strong>"{movieName}"</strong> زیرنویس مناسبی به‌صورت
                    خودکار پیدا نشد.
                  </>
                ) : (
                  <>زیرنویس مناسبی به‌صورت خودکار برای این فیلم پیدا نشد.</>
                )}
              </p>
            </div>
            <button
              className="snfm-close-btn"
              onClick={handleClose}
              title="بستن"
            >
              ✕
            </button>
          </div>

          {/* محتوای اسکرول‌شونده */}
          <div className="snfm-content">
            {/* آکوردین پیشنهاد دیگر کاربران - فقط وقتی exactlyByAnotherUser */}
            {suggestions.length > 0 && (
              <div className="snfm-suggestions-accordion">
                <button
                  className="snfm-accordion-header"
                  onClick={() => setShowSuggestions(!showSuggestions)}
                >
                  <i
                    className={`fas fa-chevron-down snfm-accordion-chevron ${
                      showSuggestions ? "open" : ""
                    }`}
                  ></i>
                  <span className="snfm-accordion-title">
                    <i className="fas fa-users"></i>
                    پیشنهاد دیگر کاربران
                    <span className="snfm-accordion-count">
                      {suggestions.length} مورد
                    </span>
                  </span>
                </button>

                {showSuggestions && (
                  <div className="snfm-accordion-body">
                    <p className="snfm-accordion-note">
                      این زیرنویس‌ها توسط کاربران دیگر پیشنهاد شده‌اند. می‌توانید
                      آن‌ها را امتحان کنید و ببینید با فیلم هماهنگ هست یا نه.
                    </p>
                    {suggestions.map((item, index) =>
                      renderCollectionCard(item, index),
                    )}
                  </div>
                )}
              </div>
            )}

            {/* بخش جستجوی دو زیرنویس */}
            <div className="snfm-search-section">
              <h4 className="snfm-search-section-title">
                <i className="fas fa-search"></i>
                جستجوی دو زیرنویس
              </h4>
              <p className="snfm-search-section-desc">
                بر اساس نام فیلم جستجو کنید و دو زیرنویس مناسب را انتخاب کنید.
                اطلاعات اولیه از نام فایل استخراج شده و ممکن است نیاز به اصلاح
                داشته باشد.
              </p>

              <div className="snfm-search-form">
                <div className="snfm-search-inputs">
                  <div className="snfm-input-group main-input">
                    <label>نام فیلم/سریال *</label>
                    <input
                      type="text"
                      value={searchMovieName}
                      onChange={(e) => setSearchMovieName(e.target.value)}
                      placeholder="مثال: Friends"
                      className="snfm-search-input"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") handleSearch();
                      }}
                    />
                  </div>

                  <div className="snfm-input-group">
                    <label>فصل</label>
                    <input
                      type="number"
                      value={searchSeason}
                      onChange={(e) => setSearchSeason(e.target.value)}
                      placeholder="فصل"
                      className="snfm-search-input"
                      min="1"
                    />
                  </div>

                  <div className="snfm-input-group">
                    <label>قسمت</label>
                    <input
                      type="number"
                      value={searchEpisode}
                      onChange={(e) => setSearchEpisode(e.target.value)}
                      placeholder="قسمت"
                      className="snfm-search-input"
                      min="1"
                    />
                  </div>
                </div>

                <button
                  className="snfm-search-btn"
                  onClick={handleSearch}
                  disabled={isSearching}
                >
                  {isSearching ? "⏳ در حال جستجو..." : "🔎 جستجوی مجموعه"}
                </button>
              </div>

              {/* خطای بارگذاری زیرنویس */}
              {loadError && (
                <div className="snfm-error">
                  <span>⚠️</span> {loadError}
                </div>
              )}

              {/* وضعیت جستجو */}
              {isSearching && (
                <div className="snfm-loading">
                  <div className="snfm-spinner"></div>
                  <span>در حال جستجو...</span>
                </div>
              )}

              {/* خطای جستجو */}
              {searchError && !isSearching && (
                <div className="snfm-error">
                  <span>⚠️</span> {searchError}
                </div>
              )}

              {/* نتایج جستجو */}
              {searchResults &&
                searchResults.docs &&
                searchResults.docs.length > 0 && (
                  <div className="snfm-results-section" ref={resultsRef}>
                    <div className="snfm-results-header">
                      <h4>نتایج جستجو</h4>
                      <span className="snfm-results-count">
                        {searchResults.totalDocs} مجموعه یافت شد
                      </span>
                    </div>
                    <div className="snfm-results-grid">
                      {searchResults.docs.map((doc, index) =>
                        renderCollectionCard(doc, index),
                      )}
                    </div>
                    {searchResults.totalPages > 1 && (
                      <div className="snfm-results-header" style={{ marginTop: "10px", justifyContent: "center" }}>
                        <span className="snfm-results-count">
                          صفحه {searchResults.page} از {searchResults.totalPages}
                        </span>
                      </div>
                    )}
                  </div>
                )}

              {/* بدون نتیجه */}
              {searchResults &&
                (!searchResults.docs || searchResults.docs.length === 0) &&
                !isSearching && (
                  <div className="snfm-no-results">
                    <div className="snfm-no-results-icon">🔍</div>
                    <p>نتیجه‌ای یافت نشد</p>
                    <p className="snfm-suggestion-text">
                      نام فیلم را دقیق‌تر وارد کنید یا فیلدهای فصل و قسمت را
                      تغییر دهید
                    </p>
                  </div>
                )}
            </div>
          </div>

          {/* فوتر */}
          <div className="snfm-footer">
            <label className="snfm-dont-show">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
              />
              <span>دیگر این پیام را نشان نده</span>
            </label>

            <button className="snfm-single-search-link" onClick={onSearch}>
              <i className="fas fa-file-import"></i> جستجوی تک زیرنویس
            </button>
          </div>
        </div>
      </div>

      {/* مودال تأیید همخوانی - روی ویدئو */}
      <CollectionConfirmModal
        isOpen={showConfirmModal}
        onBack={handleBack}
        onConfirm={handleConfirm}
        collectionInfo={selectedCollection}
      />
    </>
  );
};

export default SubtitleNotFoundModal;
